/**
 * Little Miss Takedown — small web components used by lmt-* sections and blocks.
 * No framework, no build step. Loaded as a module from snippets/lmt-head.liquid.
 */

const routes = () => (window.Theme && window.Theme.routes) || {};
const ajaxRoute = (url, fallback) => {
  const route = url || fallback;
  return route.endsWith('.js') ? route : `${route}.js`;
};

/** Browsers without speculation rules still prefetch the document on press. */
if (!HTMLScriptElement.supports?.('speculationrules')) {
  const prefetched = new Set();
  document.addEventListener('pointerdown', (event) => {
    if (event.button !== 0) return;
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link || link.target === '_blank' || link.hasAttribute('download')) return;
    const raw = link.getAttribute('href') || '';
    if (raw.startsWith('#') || raw.startsWith('mailto:') || raw.startsWith('tel:')) return;
    let url;
    try {
      url = new URL(link.href, location.href);
    } catch (error) {
      return;
    }
    if (url.origin !== location.origin) return;
    if (/^\/(checkout|account)(\/|$)/.test(url.pathname)) return;
    if (url.pathname === location.pathname && url.search === location.search) return;
    if (prefetched.has(url.href)) return;
    prefetched.add(url.href);
    const tag = document.createElement('link');
    tag.rel = 'prefetch';
    tag.as = 'document';
    tag.href = url.href;
    document.head.appendChild(tag);
  }, { capture: true, passive: true });
}
const eventsReady = import('@shopify/events');

/** Add variants in the background. The cart drawer opens from the cart:lines-update event. */
async function addLinesToCart(lines, sourceEl) {
  const cartAddUrl = ajaxRoute(routes().cart_add_url, '/cart/add.js');
  const cartUrl = ajaxRoute(routes().cart_url, '/cart.js');
  let resolve = () => {};
  let reject = () => {};
  const promise = new Promise((res, rej) => { resolve = res; reject = rej; });
  promise.catch(() => {});

  let EventsModule = null;
  try {
    EventsModule = await eventsReady;
    sourceEl.dispatchEvent(
      new EventsModule.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
        promise,
      })
    );
  } catch (e) {
    EventsModule = null;
  }

  const response = await fetch(cartAddUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      items: lines.map((l) => ({ id: l.id, quantity: l.quantity })),
      sections: 'cart-drawer-section',
      sections_url: `${window.location.pathname}${window.location.search}`,
    }),
  });
  const result = await response.json();
  const failed = !response.ok || (typeof result.status === 'number' && result.status >= 400);
  if (failed) {
    const error = new Error(result.description || result.message || 'Add to cart failed');
    reject(error);
    throw error;
  }

  const cart = await fetch(cartUrl, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then((cartResponse) => {
    if (!cartResponse.ok) throw new Error('Could not refresh the cart');
    return cartResponse.json();
  });

  let cartPayload = { totalQuantity: cart.item_count || 0 };
  try {
    if (EventsModule) cartPayload = EventsModule.CartLinesUpdateEvent.createCartFromAjaxResponse(cart);
  } catch (e) {
    cartPayload = { totalQuantity: cart.item_count || 0 };
  }

  resolve({
    cart: cartPayload,
    detail: {
      items: cart.items,
      source: 'lmt-bundle',
      itemCount: cart.item_count || 0,
      didError: false,
      sections: result.sections,
    },
  });
  return cart;
}

/** Tabs + hotspots for the Shop the Look section. */
class LmtLook extends HTMLElement {
  connectedCallback() {
    this.warmPhotos();
    this.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-lmt-tab]');
      if (tab) return this.selectTab(tab.dataset.lmtTab);
      const next = event.target.closest('[data-lmt-next]');
      if (next) return this.selectNext();
      const style = event.target.closest('[data-lmt-style]');
      if (style) return this.openStyle(style);
      const tipClose = event.target.closest('[data-lmt-tip-close]');
      if (tipClose) {
        tipClose.closest('dialog')?.close();
        return;
      }
      const quick = event.target.closest('[data-lmt-quick]');
      if (quick) return this.quickAdd(quick);
      const spot = event.target.closest('[data-lmt-spot]');
      if (spot) return this.selectSpot(spot);
    });
    this.addEventListener('keydown', (event) => {
      const tab = event.target.closest('[data-lmt-tab]');
      if (!tab || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      const tabs = [...this.querySelectorAll('[data-lmt-tab]')];
      const next = tabs[(tabs.indexOf(tab) + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
      next.focus();
      this.selectTab(next.dataset.lmtTab);
    });
  }

  warmPhotos() {
    this.querySelectorAll('[data-lmt-panel] img').forEach((img) => {
      if (img.complete && img.naturalWidth) return;
      const pre = new Image();
      const srcset = img.getAttribute('srcset');
      if (srcset) {
        pre.srcset = srcset;
        pre.sizes = img.getAttribute('sizes') || '100vw';
      }
      const src = img.getAttribute('src');
      if (src) pre.src = src;
    });
  }

  selectTab(id) {
    this.querySelectorAll('[data-lmt-tab]').forEach((t) => {
      const on = t.dataset.lmtTab === id;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
    });
    this.querySelectorAll('[data-lmt-panel]').forEach((p) => (p.hidden = p.dataset.lmtPanel !== id));
  }

  selectNext() {
    const tabs = [...this.querySelectorAll('[data-lmt-tab]')];
    if (tabs.length < 2) return;
    const current = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    const next = tabs[(current + 1) % tabs.length];
    this.selectTab(next.dataset.lmtTab);
  }

  openStyle(button) {
    const dialog = button._lmtTip || button.closest('[data-lmt-panel]')?.querySelector('dialog.lmt-look__tip');
    if (!dialog || dialog.open) return;
    button._lmtTip = dialog;
    if (dialog.parentElement !== document.body) document.body.append(dialog);
    if (!dialog.dataset.lmtBound) {
      dialog.addEventListener('click', (event) => {
        const closer = event.target instanceof Element ? event.target.closest('[data-lmt-tip-close]') : null;
        if (event.target === dialog || closer) dialog.close();
      });
      dialog.dataset.lmtBound = 'true';
    }
    dialog.showModal();
  }

  async quickAdd(button) {
    const data = button.closest('[data-lmt-row]')?.querySelector('[data-lmt-quick-data]');
    if (data) {
      try {
        openCardQuick({ data: JSON.parse(data.textContent) });
      } catch (error) {
        button.textContent = 'Quick add';
      }
      return;
    }
    const id = Number(button.dataset.lmtQuick);
    if (!id || button.disabled) return;
    const label = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';
    try {
      await addLinesToCart([{ id, quantity: 1 }], button);
      button.textContent = 'Added';
    } catch (error) {
      button.textContent = label;
    } finally {
      button.disabled = false;
      if (button.textContent === 'Added') {
        window.setTimeout(() => {
          button.textContent = label;
        }, 1200);
      }
    }
  }

  selectSpot(spot) {
    const panel = spot.closest('[data-lmt-panel]');
    const index = spot.dataset.lmtSpot;
    panel.querySelectorAll('[data-lmt-spot]').forEach((s) => s.setAttribute('aria-pressed', String(s === spot)));
    panel.querySelectorAll('[data-lmt-row]').forEach((r) => r.classList.toggle('is-active', r.dataset.lmtRow === index));
    panel.querySelector(`[data-lmt-row="${index}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
}

/** One size for the whole look/edit, then add every piece in that size. */
class LmtBundle extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('script[type="application/json"]');
    this.products = data ? JSON.parse(data.textContent) : [];
    this.button = this.querySelector('[data-lmt-add]');
    this.status = this.querySelector('[data-lmt-status]');
    this.button?.addEventListener('click', () => {
      if (this.dataset.lmtSizeUi === 'dialog' && this.querySelector('.lmt-bundle__size')) this.openSizes();
      else this.add();
    });
  }

  openSizes() {
    let dialog = document.querySelector('dialog.lmt-sizepick');
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.className = 'lmt-sizepick';
      dialog.innerHTML = `
        <form method="dialog" class="lmt-sizepick__sheet">
          <button type="button" class="lmt-look__tip-x" data-lmt-size-close aria-label="Close">
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
          </button>
          <p class="lmt-kicker">Your size</p>
          <div class="lmt-sizepick__sizes" data-lmt-size-options></div>
          <button type="submit" class="lmt-btn lmt-btn--solid">Add to cart</button>
        </form>
      `;
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog || event.target.closest('[data-lmt-size-close]')) dialog.close();
      });
      dialog.addEventListener('submit', (event) => {
        const size = dialog.querySelector('[data-lmt-size].is-on')?.dataset.lmtSize;
        const owner = dialog.owner;
        if (!size || !owner) {
          event.preventDefault();
          return;
        }
        const input = [...owner.querySelectorAll('input[type="radio"]')].find((radio) => radio.value === size);
        if (input) input.checked = true;
        owner.add();
      });
      dialog.querySelector('[data-lmt-size-options]').addEventListener('click', (event) => {
        const choice = event.target.closest('[data-lmt-size]');
        if (!choice) return;
        event.preventDefault();
        dialog.querySelectorAll('[data-lmt-size]').forEach((button) => button.classList.toggle('is-on', button === choice));
      });
      document.body.append(dialog);
    }
    const box = dialog.querySelector('[data-lmt-size-options]');
    const current = this.querySelector('input[type="radio"]:checked')?.value;
    box.replaceChildren();
    this.querySelectorAll('.lmt-bundle__size input').forEach((input) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'lmt-sizepick__size';
      button.dataset.lmtSize = input.value;
      button.textContent = input.value;
      if (input.value === current) button.classList.add('is-on');
      box.append(button);
    });
    if (!box.querySelector('.is-on')) box.querySelector('[data-lmt-size]')?.classList.add('is-on');
    dialog.owner = this;
    if (!dialog.open) dialog.showModal();
  }

  pickVariant(product, size) {
    const sizeIndex = product.options.findIndex((o) => /size/i.test(o));
    const available = product.variants.filter((v) => v.available);
    if (sizeIndex === -1 || !size) return available[0] || null;
    return available.find((v) => String(v.options[sizeIndex]).toLowerCase() === size.toLowerCase()) || null;
  }

  async add() {
    const size = this.querySelector('input[type="radio"]:checked')?.value || '';
    const lines = [];
    const missing = [];
    for (const product of this.products) {
      const variant = this.pickVariant(product, size);
      if (variant) lines.push({ id: variant.id, quantity: 1 });
      else missing.push(product.title);
    }
    if (!lines.length) {
      this.say(size ? `None of these pieces are available in ${size}.` : 'These pieces are sold out right now.');
      return;
    }
    this.button.disabled = true;
    const label = this.button.textContent;
    this.button.textContent = this.dataset.addingLabel || 'Adding…';
    try {
      await addLinesToCart(lines, this);
      this.say(missing.length ? `Added. Not available in ${size}: ${missing.join(', ')}.` : this.dataset.addedLabel || 'Added to your bag.');
    } catch (error) {
      this.say(error.message);
    } finally {
      this.button.disabled = false;
      this.button.textContent = label;
    }
  }

  say(message) {
    if (this.status) this.status.textContent = message;
  }
}

/** Countdown to a drop date. Hides itself once the date passes. */
class LmtCountdown extends HTMLElement {
  connectedCallback() {
    this.target = Date.parse(this.dataset.target || '');
    if (Number.isNaN(this.target)) return;
    this.tick();
    this.timer = setInterval(() => this.tick(), 30000);
  }
  disconnectedCallback() {
    clearInterval(this.timer);
  }
  tick() {
    const ms = this.target - Date.now();
    if (ms <= 0) {
      clearInterval(this.timer);
      this.querySelector('[data-lmt-count]')?.setAttribute('hidden', '');
      this.querySelector('[data-lmt-live]')?.removeAttribute('hidden');
      return;
    }
    const d = Math.floor(ms / 86400000);
    const h = Math.floor((ms % 86400000) / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const set = (k, v) => {
      const el = this.querySelector(`[data-lmt-unit="${k}"]`);
      if (el) el.textContent = String(v).padStart(2, '0');
    };
    set('d', d);
    set('h', h);
    set('m', m);
  }
}

/** Adult / Youth tabs and right-sliding submenus in the mobile photo menu. */
class LmtPhotoMenu extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-lmt-pm-tab]');
      if (tab) {
        const id = tab.dataset.lmtPmTab;
        this.querySelectorAll('[data-lmt-pm-tab]').forEach((t) => t.setAttribute('aria-selected', String(t === tab)));
        this.querySelectorAll('[data-lmt-pm-list]').forEach((list) => {
          list.hidden = list.dataset.lmtPmList !== id;
        });
        return;
      }
      const open = event.target.closest('[data-lmt-pm-open]');
      if (open) {
        const panel = this.querySelector(`[data-lmt-pm-panel="${open.dataset.lmtPmOpen}"]`);
        if (!panel) return;
        panel.hidden = false;
        requestAnimationFrame(() => panel.classList.add('is-open'));
        open.setAttribute('aria-expanded', 'true');
        return;
      }
      const back = event.target.closest('[data-lmt-pm-back]');
      if (!back) return;
      const panel = back.closest('[data-lmt-pm-panel]');
      if (!panel) return;
      panel.classList.remove('is-open');
      const opener = this.querySelector(`[data-lmt-pm-open="${panel.dataset.lmtPmPanel}"]`);
      opener?.setAttribute('aria-expanded', 'false');
      panel.addEventListener('transitionend', () => {
        if (!panel.classList.contains('is-open')) panel.hidden = true;
      }, { once: true });
    });
    this.closest('details')?.addEventListener('toggle', () => {
      if (this.closest('details')?.open) return;
      this.querySelectorAll('[data-lmt-pm-panel]').forEach((panel) => {
        panel.classList.remove('is-open');
        panel.hidden = true;
      });
    });
  }
}

function variantFor(chosen, variants) {
  return variants.find((variant) => chosen.every((name, index) => String(variant.options[index]).toLowerCase() === String(name).toLowerCase()));
}

function quickDialog() {
  let dialog = document.querySelector('dialog.lmt-qadd');
  if (dialog) return dialog;
  dialog = document.createElement('dialog');
  dialog.className = 'lmt-qadd';
  dialog.innerHTML = `
    <div class="lmt-qadd__sheet">
      <button type="button" class="lmt-qadd__x" data-lmt-qadd-close aria-label="Close">
        <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
      </button>
      <img class="lmt-qadd__img" alt="">
      <div class="lmt-qadd__copy">
        <h2 class="lmt-qadd__title"></h2>
        <p class="lmt-qadd__price"></p>
        <div class="lmt-qadd__options"></div>
        <button type="button" class="lmt-btn lmt-btn--solid lmt-qadd__submit">Add to cart</button>
        <p class="lmt-qadd__status" role="status"></p>
      </div>
    </div>
  `;
  dialog.addEventListener('click', (event) => {
    if (dialog.dataset.lmtOpening === 'true') return;
    const closer = event.target instanceof Element ? event.target.closest('[data-lmt-qadd-close]') : null;
    if (event.target === dialog || closer) dialog.close();
  });
  dialog.querySelector('.lmt-qadd__options').addEventListener('click', (event) => {
    const choice = event.target.closest('[data-lmt-qadd-value]');
    if (!choice || choice.disabled) return;
    dialog.pick?.(Number(choice.dataset.option), choice.dataset.lmtQaddValue);
  });
  dialog.querySelector('.lmt-qadd__submit').addEventListener('click', () => dialog.submitAdd?.());
  document.body.append(dialog);
  return dialog;
}

function openCardQuick(card) {
  const data = card.data || { variants: [], options: [] };
  const options = data.options || [];
  const variants = data.variants || [];
  const selectable = options.filter((option) => {
    const name = String(option.name || '').toLowerCase();
    return name !== 'title' && (option.values || []).length > 1;
  });
  if (!selectable.length) {
    const only = variants.find((variant) => variant.available) || variants[0];
    if (only?.id) return addLinesToCart([{ id: only.id, quantity: 1 }], card instanceof Element ? card : document.body);
    return;
  }
  const dialog = quickDialog();
  const colorIndex = data.colorIndex;
  const pressed = card.querySelector?.('[data-lmt-color][aria-pressed="true"]')?.dataset.lmtColor;
  const chosen = options.map((option, index) => {
    if (String(option.name || '').toLowerCase() === 'title') return option.values?.[0]?.name || 'Default Title';
    if (index === colorIndex && pressed) return pressed;
    return '';
  });

  const title = dialog.querySelector('.lmt-qadd__title');
  const price = dialog.querySelector('.lmt-qadd__price');
  const image = dialog.querySelector('.lmt-qadd__img');
  const optionBox = dialog.querySelector('.lmt-qadd__options');
  const submit = dialog.querySelector('.lmt-qadd__submit');
  const status = dialog.querySelector('.lmt-qadd__status');
  submit.dataset.adding = 'false';
  title.textContent = data.title || card.querySelector?.('.lmt-pcard__title')?.textContent?.trim() || '';
  image.alt = title.textContent;

  const valueAvailable = (index, name) => variants.some((variant) => {
    if (!variant.available) return false;
    if (String(variant.options[index]).toLowerCase() !== name.toLowerCase()) return false;
    return chosen.every((picked, idx) => idx === index || !picked || String(variant.options[idx]).toLowerCase() === String(picked).toLowerCase());
  });

  const paint = () => {
    const complete = chosen.length > 0 && chosen.every((name) => name);
    const current = complete ? variantFor(chosen, variants) : null;
    price.textContent = current?.price || '';
    image.src = current?.image || data.image || card.img?.currentSrc || card.img?.src || '';
    optionBox.replaceChildren();
    options.forEach((option, index) => {
      if (option.name.toLowerCase() === 'title') return;
      const group = document.createElement('fieldset');
      group.className = 'lmt-qadd__option';
      const legend = document.createElement('legend');
      legend.textContent = option.name;
      group.append(legend);
      const row = document.createElement('div');
      const isColor = /colou?r/i.test(option.name);
      row.className = isColor ? 'lmt-qadd__swatches' : 'lmt-qadd__pills';
      option.values.forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.lmtQaddValue = value.name;
        button.dataset.option = String(index);
        const on = String(chosen[index]).toLowerCase() === value.name.toLowerCase();
        const ok = valueAvailable(index, value.name);
        button.disabled = !ok;
        button.setAttribute('aria-pressed', String(on));
        if (isColor) {
          button.className = 'lmt-qadd__swatch';
          button.style.setProperty('--lmt-swatch', value.swatch || '#f6f6f6');
          button.setAttribute('aria-label', value.name);
          if (!value.swatch || ['#ffffff', '#f4f4f4', '#f7f7f7', '#efe6d6', '#f0c4a0'].includes(String(value.swatch).toLowerCase())) {
            button.classList.add('is-light');
          }
        } else {
          button.className = 'lmt-qadd__pill';
          button.textContent = value.name;
        }
        row.append(button);
      });
      group.append(row);
      optionBox.append(group);
    });
    const ready = complete && Boolean(current?.available);
    submit.disabled = !ready || submit.dataset.adding === 'true';
    submit.textContent = complete && !current?.available ? 'Sold out' : 'Add to cart';
    status.textContent = '';
  };

  dialog.pick = (index, name) => {
    chosen[index] = name;
    if (index === colorIndex && card.selectColor) card.selectColor(name);
    paint();
  };
  dialog.submitAdd = async () => {
    const current = variantFor(chosen, variants);
    if (!current?.available || submit.dataset.adding === 'true') return;
    submit.dataset.adding = 'true';
    submit.disabled = true;
    submit.textContent = 'Adding…';
    status.textContent = '';
    try {
      await addLinesToCart([{ id: current.id, quantity: 1 }], dialog);
      dialog.close();
    } catch (error) {
      status.textContent = error.message;
      submit.dataset.adding = 'false';
      paint();
    }
  };
  paint();
  if (dialog.open) return;
  dialog.dataset.lmtOpening = 'true';
  window.setTimeout(() => {
    if (!dialog.open) dialog.showModal();
    window.setTimeout(() => {
      dialog.dataset.lmtOpening = '';
    }, 350);
  }, 0);
}

/** Color chips swap the photo. The corner button opens the variant picker. */
class LmtProductCard extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('script[type="application/json"]');
    try {
      this.data = data ? JSON.parse(data.textContent) : { variants: [], options: [], colorIndex: null, sizeIndex: null };
    } catch (error) {
      this.data = { variants: [], options: [], colorIndex: null, sizeIndex: null };
    }
    this.img = this.querySelector('.lmt-pcard__img');
    this.imageIndex = this.data.imageIndex || 0;
    this.price = this.querySelector('[data-lmt-price]');
    this.compare = this.querySelector('[data-lmt-compare]');
    this.status = this.querySelector('[data-lmt-status]');
    this.addEventListener('click', (event) => {
      const chart = event.target.closest('.lmt-pcard__img.is-chart');
      if (chart) {
        event.preventDefault();
        openSizeChart(chart.currentSrc || chart.src, chart.alt);
        return;
      }
      const next = event.target.closest('[data-lmt-next]');
      if (next) {
        event.preventDefault();
        this.showNext();
        return;
      }
      const quick = event.target.closest('[data-lmt-quick]');
      if (quick) {
        event.preventDefault();
        event.stopPropagation();
        openCardQuick(this);
        return;
      }
      const swatch = event.target.closest('[data-lmt-color]');
      if (swatch) {
        event.preventDefault();
        this.selectColor(swatch.dataset.lmtColor);
      }
    });
    if (this.img) this.img.classList.toggle('is-chart', isSizeChartUrl(this.img.currentSrc || this.img.src || ''));
    watchCard(this);
    this.addEventListener('pointerenter', () => this.preloadNext(), { once: true });
  }

  variantsFor(color) {
    const index = this.data.colorIndex;
    if (index == null || !color) return this.data.variants;
    const wanted = color.toLowerCase();
    return this.data.variants.filter((variant) => String(variant.options[index]).toLowerCase() === wanted);
  }

  selectColor(color) {
    this.querySelectorAll('[data-lmt-color]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.lmtColor === color));
    });
    const colorName = this.querySelector('[data-lmt-color-name]');
    if (colorName) colorName.textContent = color;
    const variants = this.variantsFor(color);
    const current = variants.find((variant) => variant.available) || variants[0];
    const pictured = variants.find((variant) => variant.image);
    if (pictured?.image) this.showImage(pictured.image);
    this.showPrice(current);
  }

  showNext() {
    const images = this.data.images || [];
    if (images.length < 2) return;
    this.imageIndex = (this.imageIndex + 1) % images.length;
    this.showImage(images[this.imageIndex], this.imageIndex);
  }

  photoWidth() {
    const css = this.img?.getBoundingClientRect().width || 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const target = (css || 360) * dpr;
    const steps = [360, 480, 600, 720, 960];
    return steps.find((step) => step >= target) || 960;
  }

  sizedUrl(src, width = this.photoWidth()) {
    if (!src) return '';
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.href;
    } catch (error) {
      return src;
    }
  }

  warm(src, width) {
    const sized = this.sizedUrl(src, width);
    if (!sized) return;
    this.warmed = this.warmed || new Set();
    if (this.warmed.has(sized)) return;
    this.warmed.add(sized);
    const preload = new Image();
    preload.decoding = 'async';
    preload.src = sized;
  }

  preloadNext() {
    const images = this.data.images || [];
    if (images.length < 2) return;
    this.warm(images[(this.imageIndex + 1) % images.length]);
  }

  preloadGallery() {
    const width = this.photoWidth();
    (this.data.images || []).forEach((src) => this.warm(src, width));
    (this.data.variants || []).forEach((variant) => this.warm(variant.image, width));
  }

  showImage(src, index) {
    if (!src || !this.img) return;
    const images = this.data.images || [];
    const found = images.indexOf(src);
    this.imageIndex = index != null && index >= 0 ? index : found >= 0 ? found : this.imageIndex;
    const sized = this.sizedUrl(src);
    const apply = () => {
      this.img.removeAttribute('srcset');
      this.img.removeAttribute('sizes');
      if (this.img.src !== sized) this.img.src = sized;
      this.img.classList.toggle('is-chart', isSizeChartUrl(src));
      this.preloadNext();
    };
    const pending = new Image();
    pending.decoding = 'async';
    pending.src = sized;
    if (pending.complete && pending.naturalWidth) apply();
    else {
      pending.addEventListener('load', apply, { once: true });
      pending.addEventListener('error', apply, { once: true });
    }
  }

  showPrice(variant) {
    if (!variant || !this.price) return;
    this.price.textContent = variant.price;
    if (!this.compare) return;
    if (variant.compare) {
      this.compare.hidden = false;
      this.compare.textContent = variant.compare;
    } else {
      this.compare.hidden = true;
      this.compare.textContent = '';
    }
  }

}

class LmtJudgeReviews extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded === 'true') return;
    this.dataset.loaded = 'true';
    this.load();
  }

  async load() {
    const shop = this.dataset.shop;
    const token = this.dataset.token;
    const limit = Math.min(8, Number(this.dataset.limit) || 8);
    const status = this.querySelector('[data-lmt-status]');
    const track = this.querySelector('[data-lmt-track]');
    this.track = track;
    this.prev = this.querySelector('[data-lmt-prev]');
    this.next = this.querySelector('[data-lmt-next]');
    this.prev?.addEventListener('click', () => this.scrollByCard(-1));
    this.next?.addEventListener('click', () => this.scrollByCard(1));
    track?.addEventListener('scroll', () => this.updateNav(), { passive: true });
    window.addEventListener('resize', () => this.updateNav());
    if (this.dataset.productId) {
      if (!shop) return;
      try {
        await this.loadProduct();
      } catch (error) {
        this.closest('.lmt-reviews-section')?.setAttribute('hidden', '');
      }
      return;
    }
    if (!shop || !token || !track) {
      if (status) status.textContent = 'Add the Judge.me public token in this section.';
      return;
    }

    const query = `shop_domain=${encodeURIComponent(shop)}&api_token=${encodeURIComponent(token)}`;
    const endpoint = (name) => `https://api.judge.me/api/v1/widgets/${name}?${query}`;

    try {
      if (this.dataset.productId) {
        await this.loadProduct(endpoint);
        return;
      }
      const [carouselRes, ratingRes] = await Promise.all([
        fetch(endpoint('featured_carousel')),
        fetch(endpoint('all_reviews_rating')),
      ]);
      if (!carouselRes.ok) throw new Error('Reviews could not be loaded.');
      const carousel = await carouselRes.json();
      const rating = ratingRes.ok ? await ratingRes.json() : {};
      this.showScore(rating.all_reviews_rating);

      const doc = new DOMParser().parseFromString(carousel.featured_carousel || '', 'text/html');
      const cards = [...doc.querySelectorAll('.jdgm-carousel-item')]
        .map((item) => this.cardFrom(item))
        .filter(Boolean)
        .slice(0, limit);

      if (!cards.length) {
        if (status) status.textContent = 'No published reviews yet.';
        return;
      }
      status?.remove();
      track.replaceChildren(...cards);
      this.updateNav();
    } catch (error) {
      if (this.dataset.productId) {
        this.closest('.lmt-reviews-section')?.setAttribute('hidden', '');
        return;
      }
      if (status) status.textContent = 'Reviews are unavailable right now.';
    }
  }

  async loadProduct() {
    const first = await this.fetchReviewPage(1);
    const count = Number(first.number_of_reviews) || 0;
    const reviews = Array.isArray(first.reviews) ? first.reviews : [];
    if (!count || !reviews.length) {
      this.closest('.lmt-reviews-section')?.setAttribute('hidden', '');
      return;
    }
    this.reviewCount = count;
    this.average = Number(first.average_rating) || 0;
    this.page = 1;
    this.totalPages = Number(first.pagination?.total_pages) || 1;
    this.reviews = reviews.map((review) => this.normalizeReview(review));
    this.gallery = this.galleryFrom(first.photo_gallery, this.reviews);
    this.filter = 'all';
    this.visible = window.matchMedia('(max-width: 749px)').matches ? 3 : 4;
    this.renderProduct();
    this.syncRatingLink();
    this.closest('.lmt-reviews-section')?.removeAttribute('hidden');
  }

  syncRatingLink() {
    if (!(this.reviewCount > 0)) return;
    document.querySelectorAll('[data-lmt-rating-link]').forEach((link) => {
      const score = link.querySelector('[data-lmt-rating-score]');
      const count = link.querySelector('[data-lmt-rating-count]');
      if (score) score.textContent = `(${this.average.toFixed(1)})`;
      if (count) count.textContent = `[${this.reviewCount}] review${this.reviewCount === 1 ? '' : 's'}`;
      const stars = link.querySelector('.lmt-reviews__stars');
      const next = this.stars(Math.round(this.average));
      if (stars) stars.replaceWith(next);
      else link.prepend(next);
      link.hidden = false;
    });
  }

  async fetchReviewPage(page) {
    const url = `https://api.judge.me/reviews/reviews_for_widget?shop_domain=${encodeURIComponent(this.dataset.shop)}&platform=shopify&page=${page}&per_page=10&product_id=${encodeURIComponent(this.dataset.productId)}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Reviews could not be loaded.');
    return response.json();
  }

  normalizeReview(review) {
    const answers = Array.isArray(review.cf_answers) ? review.cf_answers : [];
    const fields = answers.map((answer) => {
      if (!answer || typeof answer !== 'object') return null;
      const question = String(answer.question || answer.question_title || answer.title || answer.cf_question_title || '');
      const value = String(answer.value || answer.answer || answer.cf_answer || answer.answer_value || '').trim();
      return value ? { question, value } : null;
    }).filter(Boolean);
    const sizeField = fields.find((field) => /size/i.test(field.question));
    const fitField = fields.find((field) => /fit/i.test(field.question));
    const size = (sizeField && sizeField.value) || this.sizeFromVariant(review.product_variant_title);
    const pictures = (review.pictures_urls || []).map((picture) => ({
      full: this.pictureUrl(picture, 'huge'),
      thumb: this.pictureUrl(picture, 'compact') || this.pictureUrl(picture, 'small'),
    })).filter((picture) => picture.full);
    return {
      id: review.uuid || '',
      title: String(review.title || '').trim(),
      body: this.plain(review.body_html),
      name: String(review.reviewer_name || 'Customer').trim(),
      rating: Number(review.rating) || 0,
      verified: Boolean(review.verified_buyer),
      date: review.created_at || '',
      size,
      sizeGroup: this.sizeGroup(size),
      fit: fitField ? fitField.value : '',
      pictures,
    };
  }

  galleryFrom(gallery, reviews) {
    const photos = [];
    if (Array.isArray(gallery) && gallery.length) {
      gallery.forEach((item) => {
        (item.pictures_urls || []).forEach((picture) => {
          const src = this.pictureUrl(picture, 'huge');
          if (src) photos.push({ src, id: item.uuid || '' });
        });
      });
      return photos;
    }
    reviews.forEach((review) => {
      (review.pictures || []).forEach((picture) => {
        const src = picture.full || picture;
        if (src) photos.push({ src, id: review.id });
      });
    });
    return photos;
  }

  pictureUrl(picture, size) {
    if (!picture) return '';
    if (typeof picture === 'string') return picture;
    return picture[size] || picture.original || picture.huge || picture.compact || picture.small || '';
  }

  plain(html) {
    return new DOMParser().parseFromString(html || '', 'text/html').body.textContent.replace(/\s+/g, ' ').trim();
  }

  sizeFromVariant(title) {
    const part = String(title || '').split('/').pop().trim();
    return this.sizeGroup(part) ? part : '';
  }

  sizeGroup(value) {
    const token = String(value || '').trim().toUpperCase().replace(/\s+/g, '');
    if (!token) return '';
    if (['XXS', 'XS', 'S', 'M', 'SMALL', 'MEDIUM'].includes(token)) return 'sm';
    if (['L', 'XL', 'XXL', '2XL', '3XL', '4XL', '5XL', 'LARGE', 'XLARGE'].includes(token)) return 'lxl';
    return '';
  }

  renderProduct() {
    const heading = this.dataset.heading || 'Reviews';
    const countLabel = `${this.reviewCount} review${this.reviewCount === 1 ? '' : 's'}`;
    const fit = this.fitLine();
    const write = document.createElement('a');
    write.className = 'lmt-rev__write';
    write.href = `https://judge.me/reviews/new?shop_domain=${encodeURIComponent(this.dataset.shop)}&platform=shopify&product_id=${encodeURIComponent(this.dataset.productId)}`;
    write.target = '_blank';
    write.rel = 'noopener';
    write.textContent = 'Write a review';

    const summary = document.createElement('div');
    summary.className = 'lmt-rev__summary';
    const title = document.createElement('h2');
    title.className = 'lmt-rev__heading';
    title.textContent = heading;
    const score = document.createElement('p');
    score.className = 'lmt-rev__score';
    score.textContent = `[${this.average.toFixed(1)}]`;
    const stars = this.stars(Math.round(this.average));
    stars.classList.add('lmt-rev__summary-stars');
    const based = document.createElement('p');
    based.className = 'lmt-rev__based';
    based.textContent = `Based on ${countLabel}${fit ? ` · ${fit}` : ''}`;
    const mobile = document.createElement('div');
    mobile.className = 'lmt-rev__mobile-line';
    const mobileText = document.createElement('span');
    mobileText.textContent = `[${this.average.toFixed(1)}] · ${countLabel}${fit ? ` · ${fit.replace('say fit is ', 'say ')}` : ''}`;
    mobile.append(this.stars(Math.round(this.average)), mobileText);
    summary.append(title, score, stars, based, mobile);

    const photos = document.createElement('div');
    photos.className = 'lmt-rev__photos';
    const kicker = document.createElement('p');
    kicker.className = 'lmt-rev__kicker';
    kicker.textContent = 'Customer photos';
    this.film = document.createElement('div');
    this.film.className = 'lmt-rev__film';
    photos.append(kicker, this.film);
    if (!this.gallery.length) photos.hidden = true;

    this.filters = document.createElement('div');
    this.filters.className = 'lmt-rev__filters';
    this.filters.addEventListener('click', (event) => {
      const button = event.target.closest('[data-lmt-filter]');
      if (!button) return;
      this.filter = button.dataset.lmtFilter;
      this.visible = window.matchMedia('(max-width: 749px)').matches ? 3 : 4;
      this.paintFilters();
      this.paintList();
    });

    this.list = document.createElement('div');
    this.list.className = 'lmt-rev__list';
    this.more = document.createElement('button');
    this.more.type = 'button';
    this.more.className = 'lmt-rev__more';
    this.more.addEventListener('click', () => this.showMore());
    const moreWide = document.createElement('span');
    moreWide.className = 'lmt-rev__more-wide';
    moreWide.textContent = 'Load more reviews';
    const moreShort = document.createElement('span');
    moreShort.className = 'lmt-rev__more-short';
    moreShort.textContent = `See all ${this.reviewCount} reviews`;
    this.more.append(moreWide, moreShort);

    this.replaceChildren(summary, photos, this.filters, this.list, this.more, write);
    this.paintFilm();
    this.paintFilters();
    this.paintList();
  }

  fitLine() {
    const answers = this.reviews.map((review) => review.fit).filter(Boolean);
    if (!answers.length) return '';
    const matched = answers.filter((value) => /true to size|oversized|as expected/i.test(value)).length;
    return `${Math.round((matched / answers.length) * 100)}% say fit is true to oversized`;
  }

  filteredReviews() {
    return this.reviews.filter((review) => {
      if (this.filter === 'photos') return review.pictures.length > 0;
      if (this.filter === 'five') return review.rating >= 5;
      if (this.filter === 'sm' || this.filter === 'lxl') return review.sizeGroup === this.filter;
      return true;
    });
  }

  paintFilters() {
    const withPhotos = this.reviews.filter((review) => review.pictures.length).length;
    const hasSm = this.reviews.some((review) => review.sizeGroup === 'sm');
    const hasLxl = this.reviews.some((review) => review.sizeGroup === 'lxl');
    const options = [
      ['all', 'All reviews', 'All'],
      ['photos', `With photos (${withPhotos})`, 'With photos'],
    ];
    options.push(['five', '5 stars', '5 stars']);
    if (hasSm) options.push(['sm', 'Size S–M', 'S–M']);
    if (hasLxl) options.push(['lxl', 'Size L–XL', 'L–XL']);
    this.filters.replaceChildren(...options.filter((option) => option[0] !== 'photos' || withPhotos || this.filter === 'photos').map(([id, wide, short]) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.lmtFilter = id;
      button.className = 'lmt-rev__filter';
      if (id === 'photos') button.classList.add('lmt-rev__filter--photo');
      if (id === this.filter) button.classList.add('is-on');
      button.setAttribute('aria-pressed', id === this.filter ? 'true' : 'false');
      const wideLabel = document.createElement('span');
      wideLabel.className = 'lmt-rev__filter-wide';
      wideLabel.textContent = wide;
      const shortLabel = document.createElement('span');
      shortLabel.className = 'lmt-rev__filter-short';
      shortLabel.textContent = short;
      if (id === 'photos') {
        wideLabel.prepend(this.camera());
        shortLabel.prepend(this.camera());
      }
      button.append(wideLabel, shortLabel);
      return button;
    }));
  }

  paintFilm() {
    const limit = window.matchMedia('(max-width: 749px)').matches ? 3 : 5;
    const shown = this.gallery.slice(0, limit);
    const extra = this.gallery.length - shown.length;
    const nodes = shown.map((photo) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'lmt-rev__shot';
      button.addEventListener('click', () => this.openPhoto(photo.src, 'Customer photo'));
      const image = document.createElement('img');
      image.src = photo.src;
      image.alt = 'Customer photo';
      button.append(image);
      return button;
    });
    if (extra > 0) {
      const more = document.createElement('button');
      more.type = 'button';
      more.className = 'lmt-rev__shot lmt-rev__shot--more';
      more.textContent = `+${extra} photos`;
      more.addEventListener('click', () => {
        this.filter = 'photos';
        this.visible = this.reviews.length;
        this.paintFilters();
        this.paintList();
        this.list?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      nodes.push(more);
    }
    this.film.replaceChildren(...nodes);
  }

  paintList() {
    const reviews = this.filteredReviews();
    const shown = reviews.slice(0, this.visible);
    this.list.replaceChildren(...shown.map((review) => this.reviewItem(review)));
    if (!shown.length) {
      const empty = document.createElement('p');
      empty.className = 'lmt-rev__empty';
      empty.textContent = 'No reviews match that filter.';
      this.list.append(empty);
    }
    const remaining = reviews.length - shown.length;
    const unloaded = this.filter === 'all' ? this.reviewCount - this.reviews.length : 0;
    this.more.hidden = remaining <= 0 && unloaded <= 0;
  }

  async showMore() {
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    if (mobile) this.visible = this.reviewCount;
    else this.visible += 4;
    try {
      while (this.reviews.length < Math.min(this.visible, this.reviewCount) && this.page < this.totalPages) {
        this.page += 1;
        const payload = await this.fetchReviewPage(this.page);
        const more = (payload.reviews || []).map((review) => this.normalizeReview(review));
        this.reviews.push(...more);
      }
    } catch (error) {
      this.page = Math.max(1, this.page - 1);
    }
    this.gallery = this.galleryFrom(null, this.reviews);
    this.paintFilm();
    this.paintFilters();
    this.paintList();
  }

  focusReview(id) {
    const index = this.filteredReviews().findIndex((review) => review.id === id);
    if (index >= 0) this.visible = Math.max(this.visible, index + 1);
    if (this.filter !== 'all' && index < 0) {
      this.filter = 'all';
      this.paintFilters();
      const next = this.filteredReviews().findIndex((review) => review.id === id);
      if (next >= 0) this.visible = Math.max(this.visible, next + 1);
    }
    this.paintList();
    this.querySelector(`[data-review="${CSS.escape(id)}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  reviewItem(review) {
    const item = document.createElement('article');
    item.className = 'lmt-rev__item';
    item.dataset.review = review.id;
    const who = document.createElement('div');
    who.className = 'lmt-rev__who';
    const name = document.createElement('p');
    name.className = 'lmt-rev__name';
    name.textContent = review.name;
    who.append(name);
    if (review.verified) {
      const verified = document.createElement('p');
      verified.className = 'lmt-rev__verified';
      verified.textContent = 'Verified buyer';
      who.append(verified);
    }
    if (review.size) {
      const bought = document.createElement('p');
      bought.className = 'lmt-rev__detail';
      bought.textContent = `Bought: Size ${review.size}`;
      who.append(bought);
    }
    if (review.fit) {
      const fit = document.createElement('p');
      fit.className = 'lmt-rev__detail';
      fit.textContent = `Fit: ${review.fit}`;
      who.append(fit);
    }
    const body = document.createElement('div');
    body.className = 'lmt-rev__copy';
    const head = document.createElement('div');
    head.className = 'lmt-rev__copy-head';
    head.append(this.stars(review.rating));
    if (review.pictures.length) {
      const badge = document.createElement('p');
      badge.className = 'lmt-rev__badge';
      badge.append(this.camera(), document.createTextNode('With photo'));
      head.append(badge);
    }
    const date = document.createElement('p');
    date.className = 'lmt-rev__date';
    date.textContent = this.formatDate(review.date);
    head.append(date);
    body.append(head);
    if (review.title) {
      const title = document.createElement('h3');
      title.className = 'lmt-rev__title';
      title.textContent = review.title;
      body.append(title);
    }
    if (review.body) {
      const quote = document.createElement('blockquote');
      quote.className = 'lmt-rev__quote';
      quote.textContent = review.body;
      body.append(quote);
    }
    const pics = document.createElement('div');
    pics.className = 'lmt-rev__pics';
    review.pictures.forEach((picture) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'lmt-rev__pic';
      button.addEventListener('click', () => this.openPhoto(picture.full || picture.thumb, `Photo from ${review.name}`));
      const image = document.createElement('img');
      image.src = picture.thumb || picture.full;
      image.alt = `Photo from ${review.name}`;
      button.append(image);
      pics.append(button);
    });
    item.append(who, body, pics);
    return item;
  }

  openPhoto(src, alt) {
    if (!src) return;
    let dialog = document.querySelector('dialog.lmt-rev__zoom');
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.className = 'lmt-rev__zoom';
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'lmt-rev__zoom-close';
      close.setAttribute('aria-label', 'Close photo');
      close.textContent = '×';
      close.addEventListener('click', () => dialog.close());
      const image = document.createElement('img');
      image.alt = '';
      dialog.append(close, image);
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      document.body.append(dialog);
    }
    const image = dialog.querySelector('img');
    image.src = src;
    image.alt = alt || 'Review photo';
    if (!dialog.open) dialog.showModal();
  }

  camera() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M4 8h3l2-2h6l2 2h3v11H4V8z');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.6');
    const lens = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    lens.setAttribute('cx', '12');
    lens.setAttribute('cy', '13');
    lens.setAttribute('r', '3');
    lens.setAttribute('fill', 'none');
    lens.setAttribute('stroke', 'currentColor');
    lens.setAttribute('stroke-width', '1.6');
    svg.append(path, lens);
    return svg;
  }

  formatDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  showScore(rating, count) {
    const root = this.closest('.lmt-section');
    const score = root?.querySelector('[data-lmt-score]');
    const summary = root?.querySelector('[data-lmt-summary-stars]');
    const value = Number(rating);
    if (!score || !Number.isFinite(value) || value <= 0) return;
    const valueEl = score.querySelector('[data-lmt-score-value]');
    if (valueEl) valueEl.textContent = value.toFixed(1);
    const countEl = score.querySelector('[data-lmt-score-count]');
    if (countEl && Number(count) > 0) countEl.textContent = `[${count}] reviews`;
    score.hidden = false;
    if (!summary) return;
    summary.hidden = false;
    summary.replaceChildren(...this.stars(Math.round(value)).childNodes);
  }

  scrollByCard(direction) {
    const card = this.track?.querySelector('.lmt-reviews__card');
    const gap = 16;
    const amount = (card?.getBoundingClientRect().width || 280) + gap;
    this.track?.scrollBy({ left: direction * amount, behavior: 'smooth' });
  }

  updateNav() {
    if (!this.track || !this.prev || !this.next) return;
    const max = this.track.scrollWidth - this.track.clientWidth;
    const canScroll = max > 4;
    this.prev.hidden = !canScroll || this.track.scrollLeft <= 4;
    this.next.hidden = !canScroll || this.track.scrollLeft >= max - 4;
  }

  cardFrom(item) {
    const body = item.querySelector('.jdgm-carousel-item__review-body')?.textContent.replace(/\s+/g, ' ').trim();
    if (!body) return null;
    const name = item.querySelector('.jdgm-carousel-item__reviewer-name')?.textContent.replace(/\s+/g, ' ').trim() || 'Customer';
    const stars = Number((item.querySelector('.jdgm-carousel-item__review-rating')?.getAttribute('aria-label') || '').match(/\d+/)?.[0] || 5);
    const product = item.querySelector('.jdgm-carousel-item__product-image')?.getAttribute('alt')?.trim() || '';

    const card = document.createElement('article');
    card.className = 'lmt-reviews__card';
    card.append(this.stars(stars), this.quote(body), this.byline(name, product));
    return card;
  }

  stars(count) {
    const row = document.createElement('div');
    row.className = 'lmt-reviews__stars';
    row.setAttribute('role', 'img');
    row.setAttribute('aria-label', `${count} out of 5 stars`);
    for (let i = 1; i <= 5; i += 1) {
      const star = document.createElement('span');
      if (i <= count) star.className = 'is-on';
      star.textContent = '★';
      row.append(star);
    }
    return row;
  }

  quote(body) {
    const quote = document.createElement('blockquote');
    quote.className = 'lmt-reviews__quote';
    quote.textContent = body;
    return quote;
  }

  byline(name, product) {
    const line = document.createElement('p');
    line.className = 'lmt-reviews__meta';
    line.textContent = product ? `${name} · ${product}` : name;
    return line;
  }
}

/** Build-the-edit dialog: pick each piece's size and color, then add the bundle variant. */
class LmtPackageDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.submit = this.querySelector('[data-lmt-add]');
    this.status = this.querySelector('[data-lmt-status]');
    const data = this.querySelector('script[type="application/json"]');
    this.packageProduct = data ? JSON.parse(data.textContent) : { options: [], variants: [] };
    this.querySelector('[data-lmt-open]')?.addEventListener('click', () => {
      this.dialog.showModal();
      this.refresh();
    });
    this.querySelectorAll('[data-lmt-close]').forEach((button) => {
      button.addEventListener('click', () => this.dialog.close());
    });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    this.dialog.addEventListener('change', (event) => {
      if (event.target.matches('[data-lmt-same-size]')) this.applySameSize(event.target.value);
      this.refresh();
    });
    this.submit?.addEventListener('click', () => this.add());
    this.refresh();
  }

  applySameSize(size) {
    this.querySelectorAll('[data-option-kind="size"]').forEach((group) => {
      const match = [...group.querySelectorAll('input')].find((input) => input.value === size);
      if (match) match.checked = true;
    });
  }

  chosen() {
    const values = this.packageProduct.options.map(() => '');
    this.querySelectorAll('[data-option-index]').forEach((group) => {
      const selected = group.querySelector('input:checked');
      values[Number(group.dataset.optionIndex)] = selected ? selected.value : '';
    });
    return values;
  }

  matchingVariant(values) {
    if (values.some((value) => !value)) return null;
    return this.packageProduct.variants.find((item) => item.available && values.every((value, index) => String(item.options[index]) === String(value))) || null;
  }

  refresh() {
    this.querySelectorAll('[data-lmt-piece]').forEach((piece) => {
      const meta = piece.querySelector('[data-lmt-meta]');
      if (!meta) return;
      const color = piece.querySelector('[data-option-kind="color"] input:checked')?.value || piece.dataset.fixedColor || '';
      const type = piece.dataset.productType || '';
      meta.textContent = [type, color].filter(Boolean).join(' · ');
    });

    const picked = this.querySelector('[data-lmt-same-size]:checked');
    if (picked) {
      const size = picked.value;
      const groups = [...this.querySelectorAll('[data-option-kind="size"]')].filter((group) => [...group.querySelectorAll('input')].some((input) => input.value === size));
      const allMatch = groups.length > 0 && groups.every((group) => group.querySelector('input:checked')?.value === size);
      if (!allMatch) picked.checked = false;
    }

    const values = this.chosen();
    const missingSize = [...this.querySelectorAll('[data-option-kind="size"]')].some((group) => !group.querySelector('input:checked'));
    const missingColor = [...this.querySelectorAll('[data-option-kind="color"]')].some((group) => !group.querySelector('input:checked'));
    let label = this.dataset.readyLabel || 'Add the edit';
    let ready = true;
    if (missingSize) {
      label = this.dataset.sizeLabel || 'Choose sizes to continue';
      ready = false;
    } else if (missingColor) {
      label = this.dataset.colorLabel || 'Choose a color to continue';
      ready = false;
    } else if (!this.matchingVariant(values)) {
      label = this.dataset.unavailableLabel || 'That combination is not available.';
      ready = false;
    }
    if (!this.submit) return;
    this.submit.disabled = !ready;
    this.submit.textContent = label;
  }

  async add() {
    const variant = this.matchingVariant(this.chosen());
    if (!variant || this.submit.disabled) return;
    this.submit.disabled = true;
    const label = this.submit.textContent;
    this.submit.textContent = this.dataset.addingLabel || 'Adding…';
    try {
      await addLinesToCart([{ id: variant.id, quantity: 1 }], this);
      if (this.status) this.status.textContent = '';
      this.dialog.close();
    } catch (error) {
      if (this.status) this.status.textContent = error.message;
      this.submit.disabled = false;
      this.submit.textContent = label;
    }
  }
}

/** Collection filters, sort, and load more. */
class LmtCollection extends HTMLElement {
  connectedCallback() {
    this.side = this.querySelector('[data-lmt-side]');
    this.querySelector('[data-lmt-filter-open]')?.addEventListener('click', () => this.toggleFilters(true));
    this.querySelectorAll('[data-lmt-filter-close]').forEach((button) => {
      button.addEventListener('click', () => this.toggleFilters(false));
    });
    this.querySelectorAll('[data-lmt-filter]').forEach((input) => {
      input.addEventListener('change', () => input.form?.requestSubmit());
    });
    this.querySelectorAll('[data-lmt-type-family]').forEach((group) => {
      const toggle = group.querySelector('[data-lmt-type-toggle]');
      toggle?.addEventListener('change', () => {
        const inputs = [...group.querySelectorAll('input[name]')];
        if (!inputs.length) return;
        inputs.forEach((input) => {
          input.checked = toggle.checked;
        });
        inputs[0].form?.requestSubmit();
      });
    });
    this.querySelectorAll('[data-lmt-color-family]').forEach((group) => {
      const button = group.querySelector('button');
      button?.addEventListener('click', () => {
        const inputs = [...group.querySelectorAll('input')];
        if (!inputs.length || button.disabled) return;
        const turnOn = button.getAttribute('aria-pressed') !== 'true';
        inputs.forEach((input) => {
          input.checked = turnOn;
        });
        group.classList.toggle('is-on', turnOn);
        button.setAttribute('aria-pressed', String(turnOn));
        inputs[0].form?.requestSubmit();
      });
    });
    this.querySelector('[data-lmt-sort]')?.addEventListener('change', (event) => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.delete('page');
      window.location.assign(url);
    });
    this.more = this.querySelector('[data-lmt-more]');
    this.loadingMore = false;
    this.moreVisible = false;
    if (this.more && 'IntersectionObserver' in window) {
      this.moreObserver = new IntersectionObserver((entries) => {
        this.moreVisible = entries.some((entry) => entry.isIntersecting);
        if (this.moreVisible) this.loadMore();
      }, { rootMargin: '240px 0px' });
      this.moreObserver.observe(this.more);
    }
    this.bindPrice();
    this.bindAudience();
  }

  bindAudience() {
    if (this.dataset.lmtAudienceBound === 'true') return;
    const picks = [...this.querySelectorAll('[data-lmt-audience-pick]')];
    if (!picks.length) return;
    this.dataset.lmtAudienceBound = 'true';
    const apply = (audience, updateUrl) => {
      this.classList.toggle('is-audience-adult', audience === 'adult');
      this.classList.toggle('is-audience-youth', audience === 'youth');
      picks.forEach((link) => {
        if (link.dataset.lmtAudiencePick === audience) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      const budget = this.querySelector('[data-lmt-budget]');
      let budgetVisible = false;
      if (budget) {
        if (audience === 'all') budgetVisible = true;
        else {
          for (const card of budget.querySelectorAll('[data-lmt-audience]')) {
            if (card.dataset.lmtAudience === audience) {
              budgetVisible = true;
              break;
            }
          }
        }
        budget.hidden = !budgetVisible;
      }
      let visible = audience === 'all' || budgetVisible;
      if (!visible) {
        for (const card of this.querySelectorAll('[data-lmt-grid] [data-lmt-audience]')) {
          if (card.dataset.lmtAudience === audience) {
            visible = true;
            break;
          }
        }
      }
      this.classList.toggle('is-audience-empty', !visible);
      const form = this.querySelector('.lmt-col__filters');
      if (form) {
        let input = form.querySelector('input[name="audience"]');
        if (audience === 'all') {
          input?.remove();
        } else if (input) {
          input.value = audience;
        } else {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'audience';
          input.value = audience;
          form.appendChild(input);
        }
      }
      if (!updateUrl) return;
      const url = new URL(window.location.href);
      if (audience === 'all') url.searchParams.delete('audience');
      else url.searchParams.set('audience', audience);
      history.replaceState(null, '', url);
    };
    const onTagPage = /\/(adult|youth)\/?$/.test(window.location.pathname);
    picks.forEach((link) => {
      link.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        if (onTagPage) return;
        event.preventDefault();
        apply(link.dataset.lmtAudiencePick || 'all', true);
      });
    });
    const initial = new URLSearchParams(window.location.search).get('audience');
    if (initial === 'adult' || initial === 'youth') apply(initial, false);
  }

  toggleFilters(open) {
    this.side?.classList.toggle('is-open', open);
    this.classList.toggle('is-filtering', open);
    document.body.classList.toggle('lmt-col-lock', open);
    if (open) this.pricePaint?.();
  }

  bindPrice() {
    const box = this.querySelector('[data-lmt-price]');
    if (!box) return;
    const min = box.querySelector('[data-lmt-price-min]');
    const max = box.querySelector('[data-lmt-price-max]');
    const label = box.querySelector('[data-lmt-price-label]');
    const fill = box.querySelector('[data-lmt-price-fill]');
    const thumb = 16;
    const paint = () => {
      if (!fill) return;
      const span = Number(min.max) - Number(min.min) || 1;
      const width = min.getBoundingClientRect().width || min.offsetWidth;
      const lo = (Number(min.value) - Number(min.min)) / span;
      const hi = (Number(max.value) - Number(min.min)) / span;
      const start = lo * (width - thumb) + thumb / 2;
      const end = hi * (width - thumb) + thumb / 2;
      fill.style.left = `${start}px`;
      fill.style.width = `${Math.max(0, end - start)}px`;
    };
    const sync = (active) => {
      let lo = Number(min.value);
      let hi = Number(max.value);
      if (lo > hi) {
        if (active === min) lo = hi;
        else hi = lo;
        min.value = String(lo);
        max.value = String(hi);
      }
      if (label) label.textContent = `$${lo} – $${hi}`;
      if (lo > Number(min.min)) min.name = min.dataset.name;
      else min.removeAttribute('name');
      if (hi < Number(max.max)) max.name = max.dataset.name;
      else max.removeAttribute('name');
      paint();
    };
    const submit = () => box.closest('form')?.requestSubmit();
    min.addEventListener('input', () => sync(min));
    max.addEventListener('input', () => sync(max));
    min.addEventListener('pointerdown', () => {
      min.style.zIndex = '2';
      max.style.zIndex = '1';
    });
    max.addEventListener('pointerdown', () => {
      max.style.zIndex = '2';
      min.style.zIndex = '1';
    });
    min.addEventListener('change', submit);
    max.addEventListener('change', submit);
    this.pricePaint = paint;
    sync();
    requestAnimationFrame(paint);
    window.addEventListener('resize', paint);
  }

  async loadMore() {
    const sentinel = this.more;
    const grid = this.querySelector('[data-lmt-grid]');
    const href = sentinel?.dataset.href;
    if (!sentinel || !grid || !href || this.loadingMore) return;
    this.loadingMore = true;
    let keepGoing = false;
    sentinel.textContent = 'Loading more';
    sentinel.setAttribute('aria-busy', 'true');
    try {
      const html = await fetch(href).then((response) => {
        if (!response.ok) throw new Error('Could not load more products');
        return response.text();
      });
      const doc = new DOMParser().parseFromString(html, 'text/html');
      doc.querySelectorAll('[data-lmt-grid] [data-lmt-card]').forEach((card) => grid.append(card));
      const next = doc.querySelector('[data-lmt-more]');
      if (next?.dataset.href) {
        sentinel.dataset.href = next.dataset.href;
        sentinel.textContent = '';
        sentinel.removeAttribute('aria-busy');
        keepGoing = true;
      } else {
        this.moreObserver?.disconnect();
        sentinel.remove();
        this.more = null;
      }
    } catch (error) {
      sentinel.textContent = '';
      sentinel.removeAttribute('aria-busy');
    } finally {
      this.loadingMore = false;
      if (keepGoing && this.more && this.moreVisible) this.loadMore();
    }
  }
}

const define = (name, ctor) => customElements.get(name) || customElements.define(name, ctor);
define('lmt-look', LmtLook);
define('lmt-bundle', LmtBundle);
define('lmt-package-dialog', LmtPackageDialog);
define('lmt-collection', LmtCollection);
define('lmt-countdown', LmtCountdown);
define('lmt-photo-menu', LmtPhotoMenu);
define('lmt-product-card', LmtProductCard);
define('lmt-judge-reviews', LmtJudgeReviews);

function bindShare() {
  document.querySelectorAll('[data-lmt-share]').forEach((button) => {
    if (button.dataset.lmtBound === 'true') return;
    button.dataset.lmtBound = 'true';
    const label = button.querySelector('[data-lmt-share-label]');
    const original = label ? label.textContent : 'Share';
    button.addEventListener('click', async () => {
      const url = button.dataset.url || window.location.href;
      const title = button.dataset.title || document.title;
      if (navigator.share) {
        try {
          await navigator.share({ title, url });
          return;
        } catch (error) {
          if (error && error.name === 'AbortError') return;
        }
      }
      try {
        await navigator.clipboard.writeText(url);
        if (label) label.textContent = 'Copied';
        window.setTimeout(() => {
          if (label) label.textContent = original;
        }, 1600);
      } catch (error) {
        window.prompt('Copy this link', url);
      }
    });
  });
}

function bindCartFeedback() {
  const bar = document.querySelector('[data-lmt-cartbar]');
  if (!bar) return;
  const countEl = bar.querySelector('[data-lmt-cartbar-count]');
  const totalEl = bar.querySelector('[data-lmt-cartbar-total]');
  let count = Number(bar.dataset.count || 0);
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const mobileQuery = window.matchMedia('(max-width: 749px)');
  let idleTimer = 0;

  const place = (settled) => {
    const eligible = count > 0 && window.scrollY > 200;
    const scrolling = mobileQuery.matches && !settled;
    if (!eligible || scrolling) {
      if (mobileQuery.matches) bar.classList.add('is-instant');
      bar.classList.remove('is-on');
      return;
    }
    bar.classList.remove('is-instant');
    if (bar.classList.contains('is-on')) return;
    void bar.offsetWidth;
    bar.classList.add('is-on');
  };

  const shake = () => {
    if (reduced) return;
    const icon = document.querySelector('cart-icon');
    if (!icon) return;
    icon.classList.remove('lmt-cart-shake');
    void icon.offsetWidth;
    icon.classList.add('lmt-cart-shake');
  };

  const money = (cents, currency) => {
    const formatted = new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: currency || 'USD',
    }).format((cents || 0) / 100);
    return formatted.replace(/\.00$/, '');
  };

  window.addEventListener('scroll', () => {
    if (!mobileQuery.matches) {
      place(true);
      return;
    }
    place(false);
    window.clearTimeout(idleTimer);
    idleTimer = window.setTimeout(() => place(true), 200);
  }, { passive: true });
  place(true);

  bar.querySelector('[data-lmt-cartbar-open]')?.addEventListener('click', () => {
    const trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
    if (trigger) trigger.click();
    else window.location.assign(bar.dataset.cartUrl || '/cart');
  });

  import('@shopify/events').then(({ StandardEvents }) => {
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      event.promise?.then(async ({ cart, detail }) => {
        const next = cart?.totalQuantity ?? detail?.itemCount;
        if (typeof next === 'number' && next > count) shake();
        try {
          const fresh = await fetch(`${bar.dataset.cartUrl || '/cart'}.js`, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          }).then((response) => response.json());
          count = fresh.item_count || 0;
          if (countEl) countEl.textContent = String(count);
          if (totalEl) totalEl.textContent = money(fresh.total_price, fresh.currency);
        } catch (error) {
          if (typeof next === 'number') {
            count = next;
            if (countEl) countEl.textContent = String(count);
          }
        }
        place(true);
      }).catch(() => {});
    });
  }).catch(() => {});
}

function watchCard(card) {
  if (!watchCard.observer) {
    watchCard.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.preloadNext?.();
        watchCard.observer.unobserve(entry.target);
      });
    }, { rootMargin: '1200px 0px' });
  }
  watchCard.observer.observe(card);
}

function primeUpcomingImages() {
  if (!('IntersectionObserver' in window) || !document.body) return;
  if (!primeUpcomingImages.observer) {
    primeUpcomingImages.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const img = entry.target;
        if (!(img instanceof HTMLImageElement)) return;
        if (img.getAttribute('fetchpriority') === 'low') img.fetchPriority = 'auto';
        if (img.loading === 'lazy') img.loading = 'eager';
        primeUpcomingImages.observer.unobserve(img);
      });
    }, { rootMargin: '1200px 0px' });
  }

  const watch = (img) => {
    if (!(img instanceof HTMLImageElement)) return;
    if (img.loading !== 'lazy' || img.dataset.lmtPrimed) return;
    if (img.closest('dialog:not([open]), [hidden], template')) return;
    img.dataset.lmtPrimed = '1';
    primeUpcomingImages.observer.observe(img);
  };

  document.querySelectorAll('img[loading="lazy"]').forEach(watch);

  if (!primeUpcomingImages.mutations) {
    primeUpcomingImages.mutations = new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node instanceof HTMLImageElement) watch(node);
          else if (node instanceof Element) node.querySelectorAll('img[loading="lazy"]').forEach(watch);
        });
      });
    });
    primeUpcomingImages.mutations.observe(document.body, { childList: true, subtree: true });
  }
}

function isSizeChartUrl(src) {
  return /size-guide|size_guide|size-chart|sizechart/i.test(src || '');
}

function isSizeChartMedia(media, img) {
  if (isSizeChartUrl(`${img.getAttribute('src') || ''} ${img.getAttribute('srcset') || ''} ${img.currentSrc || ''}`)) return true;
  const ratio = parseFloat((media.getAttribute('style') || '').match(/--ratio:\s*([0-9.]+)/)?.[1] || '');
  if (ratio >= 2.2) return true;
  if (img.naturalWidth > 0 && img.naturalHeight > 0 && img.naturalWidth / img.naturalHeight >= 2.2) return true;
  return false;
}

function attachPanZoom(stage, img) {
  let scale = 1;
  let x = 0;
  let y = 0;
  let dragging = false;
  let moved = false;
  let startX = 0;
  let startY = 0;
  let originX = 0;
  let originY = 0;
  const min = 1;
  const max = 5;

  const render = () => {
    img.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    stage.classList.toggle('is-zoomed', scale > 1);
    stage.classList.toggle('is-dragging', dragging);
  };

  const clamp = () => {
    const rect = stage.getBoundingClientRect();
    const maxX = ((scale - 1) * rect.width) / 2;
    const maxY = ((scale - 1) * rect.height) / 2;
    x = Math.min(maxX, Math.max(-maxX, x));
    y = Math.min(maxY, Math.max(-maxY, y));
  };

  const setScale = (next, cx, cy) => {
    const prev = scale || 1;
    scale = Math.min(max, Math.max(min, next));
    if (scale === 1) {
      x = 0;
      y = 0;
    } else if (cx != null && prev > 0) {
      const rect = stage.getBoundingClientRect();
      const ox = cx - (rect.left + rect.width / 2);
      const oy = cy - (rect.top + rect.height / 2);
      const factor = scale / prev;
      x = ox - (ox - x) * factor;
      y = oy - (oy - y) * factor;
      clamp();
    } else {
      clamp();
    }
    render();
  };

  stage.addEventListener('wheel', (event) => {
    event.preventDefault();
    event.stopPropagation();
    setScale(scale * (event.deltaY < 0 ? 1.15 : 1 / 1.15), event.clientX, event.clientY);
  }, { passive: false });

  stage.addEventListener('dblclick', (event) => {
    event.preventDefault();
    event.stopPropagation();
    setScale(scale > 1 ? 1 : 2.4, event.clientX, event.clientY);
  });

  stage.addEventListener('pointerdown', (event) => {
    if (event.button !== 0 || scale <= 1 || event.target.closest('button')) return;
    dragging = true;
    moved = false;
    startX = event.clientX;
    startY = event.clientY;
    originX = x;
    originY = y;
    stage.setPointerCapture(event.pointerId);
    render();
  });

  stage.addEventListener('pointermove', (event) => {
    if (!dragging) return;
    const dx = event.clientX - startX;
    const dy = event.clientY - startY;
    if (Math.abs(dx) + Math.abs(dy) > 3) moved = true;
    x = originX + dx;
    y = originY + dy;
    clamp();
    render();
  });

  const endDrag = () => {
    if (!dragging) return;
    dragging = false;
    render();
    if (moved) {
      stage.dataset.lmtDragged = '1';
      window.setTimeout(() => { stage.dataset.lmtDragged = ''; }, 0);
    }
  };
  stage.addEventListener('pointerup', endDrag);
  stage.addEventListener('pointercancel', endDrag);
  stage.addEventListener('click', (event) => {
    if (stage.dataset.lmtDragged !== '1') return;
    event.preventDefault();
    event.stopPropagation();
  });

  let pinchDist = 0;
  let pinchScale = 1;
  const pinchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy) || 1;
  };
  stage.addEventListener('touchstart', (event) => {
    if (event.touches.length === 2) {
      pinchDist = pinchDistance(event.touches);
      pinchScale = scale;
    }
  }, { passive: true });
  stage.addEventListener('touchmove', (event) => {
    if (event.touches.length !== 2 || !pinchDist) return;
    event.preventDefault();
    setScale(pinchScale * (pinchDistance(event.touches) / pinchDist));
  }, { passive: false });

  stage.querySelectorAll('[data-lmt-zoom]').forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      setScale(button.dataset.lmtZoom === 'in' ? scale * 1.4 : scale / 1.4);
    });
  });

  return { reset: () => setScale(1) };
}

function zoomTools() {
  const tools = document.createElement('div');
  tools.className = 'lmt-size-guide__tools';
  tools.innerHTML = '<button type="button" data-lmt-zoom="out" aria-label="Zoom out">−</button><button type="button" data-lmt-zoom="in" aria-label="Zoom in">+</button>';
  return tools;
}

function mountGalleryChart(media, img) {
  media.classList.add('lmt-size-guide');
  media.dataset.lmtChart = 'on';
  img.draggable = false;
  const slide = media.closest('[on\\:click]');
  if (slide && (slide.getAttribute('on:click') || '').includes('zoom-dialog')) slide.removeAttribute('on:click');
  if (!media.querySelector('.lmt-size-guide__tools')) media.append(zoomTools());
  attachPanZoom(media, img);
}

function lockChartPage() {
  const root = document.documentElement;
  if (root.classList.contains('lmt-chart-open')) return;
  const y = window.scrollY;
  root.classList.add('lmt-chart-open');
  root.dataset.lmtChartY = String(y);
  const pin = () => {
    if (window.scrollY !== y) window.scrollTo(0, y);
  };
  const block = (event) => {
    event.preventDefault();
  };
  root.lmtChartPin = pin;
  root.lmtChartBlock = block;
  window.addEventListener('scroll', pin, { passive: true });
  document.addEventListener('touchmove', block, { passive: false, capture: true });
  document.addEventListener('wheel', block, { passive: false, capture: true });
}

function unlockChartPage() {
  const root = document.documentElement;
  if (!root.classList.contains('lmt-chart-open')) return;
  const y = Number(root.dataset.lmtChartY || 0);
  if (root.lmtChartPin) window.removeEventListener('scroll', root.lmtChartPin);
  if (root.lmtChartBlock) {
    document.removeEventListener('touchmove', root.lmtChartBlock, { capture: true });
    document.removeEventListener('wheel', root.lmtChartBlock, { capture: true });
  }
  delete root.lmtChartPin;
  delete root.lmtChartBlock;
  root.classList.remove('lmt-chart-open');
  delete root.dataset.lmtChartY;
  window.scrollTo(0, y);
}

function openSizeChart(src, alt) {
  let dialog = document.querySelector('dialog.lmt-chart');
  if (!dialog) {
    dialog = document.createElement('dialog');
    dialog.className = 'lmt-chart';
    dialog.innerHTML = '<div class="lmt-chart__stage" data-lmt-chart-stage><img alt="Size chart" data-lmt-chart-img draggable="false"></div>';
    const stage = dialog.querySelector('[data-lmt-chart-stage]');
    const tools = zoomTools();
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'lmt-chart__close';
    close.setAttribute('aria-label', 'Close');
    close.textContent = '×';
    close.addEventListener('click', () => dialog.close());
    tools.append(close);
    stage.append(tools);
    document.body.append(dialog);
    dialog._pan = attachPanZoom(stage, dialog.querySelector('[data-lmt-chart-img]'));
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      dialog._pan?.reset();
      unlockChartPage();
    });
  }
  const image = dialog.querySelector('[data-lmt-chart-img]');
  image.alt = alt || 'Size chart';
  try {
    const full = new URL(src, window.location.origin);
    full.searchParams.set('width', '1400');
    image.src = full.href;
  } catch (error) {
    image.src = src;
  }
  dialog._pan?.reset();
  const fit = () => {
    const ratio = image.naturalWidth / image.naturalHeight;
    if (!ratio) return;
    const viewportWidth = window.visualViewport?.width || window.innerWidth;
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const width = Math.min(960, viewportWidth - 32);
    const height = Math.min(viewportHeight - 32, Math.max(220, width / ratio));
    dialog.style.width = `${Math.round(width)}px`;
    dialog.style.height = `${Math.ceil(height)}px`;
  };
  if (image.complete) fit();
  else image.addEventListener('load', fit, { once: true });
  lockChartPage();
  if (!dialog.open) dialog.showModal();
}

document.addEventListener('click', (event) => {
  const link = event.target.closest?.('[data-lmt-size-guide-src]');
  if (!link) return;
  event.preventDefault();
  openSizeChart(link.getAttribute('data-lmt-size-guide-src'), link.getAttribute('data-lmt-size-guide-alt') || 'Size guide');
});

function bindSizeCharts(root = document) {
  root.querySelectorAll('.product-media').forEach((media) => {
    if (media.dataset.lmtChart || media.closest('.dialog-zoomed-gallery, .dialog-thumbnails-list')) return;
    const img = media.querySelector('img.product-media__image');
    if (!img) return;
    const apply = () => {
      if (media.dataset.lmtChart || !isSizeChartMedia(media, img)) return;
      mountGalleryChart(media, img);
    };
    apply();
    if (!img.complete) img.addEventListener('load', apply, { once: true });
  });

  root.querySelectorAll('img').forEach((img) => {
    if (img.closest('.lmt-pcard, .product-media.lmt-size-guide, dialog.lmt-chart')) return;
    const src = `${img.getAttribute('src') || ''} ${img.getAttribute('srcset') || ''}`;
    if (!isSizeChartUrl(src)) return;
    const thumb = img.closest('button, .dialog-thumbnails-list__thumbnail');
    if (thumb) thumb.classList.add('lmt-chart-thumb');
  });
}

let sizeChartFrame = 0;
function scheduleSizeCharts() {
  window.cancelAnimationFrame(sizeChartFrame);
  sizeChartFrame = window.requestAnimationFrame(() => bindSizeCharts());
}

bindSizeCharts();
new MutationObserver(scheduleSizeCharts).observe(document.documentElement, { childList: true, subtree: true });

function formatShipMoney(amount, currency) {
  return new Intl.NumberFormat(document.documentElement.lang || undefined, {
    style: 'currency',
    currency: currency || 'USD',
  }).format(amount);
}

function updateCartShipping(root = document) {
  root.querySelectorAll('[data-lmt-cart-ship]').forEach((row) => {
    const value = row.querySelector('[data-lmt-cart-ship-value]');
    if (!value) return;
    const total = Number(row.dataset.total || 0);
    const threshold = Number(row.dataset.threshold || 8000);
    if (threshold === 0 || total >= threshold) {
      if (value.textContent !== 'Free') value.textContent = 'Free';
      row.querySelector('.lmt-cart__ship-hint')?.remove();
      return;
    }
    const token = [row.dataset.zip, row.dataset.country, row.dataset.province, total].join('|');
    if (row.dataset.lmtShipToken === token) return;
    row.dataset.lmtShipToken = token;
    const requestId = String(Date.now());
    row.dataset.lmtShipReq = requestId;
    const params = new URLSearchParams();
    params.set('shipping_address[zip]', row.dataset.zip || '10001');
    params.set('shipping_address[country]', row.dataset.country || 'United States');
    params.set('shipping_address[province]', row.dataset.province || 'NY');
    fetch(`/cart/shipping_rates.json?${params.toString()}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error('rates');
        return response.json();
      })
      .then((data) => {
        if (row.dataset.lmtShipReq !== requestId) return;
        const rates = Array.isArray(data.shipping_rates) ? data.shipping_rates : [];
        if (!rates.length) throw new Error('empty');
        const lowest = rates.reduce((best, rate) => (Number(rate.price) < Number(best.price) ? rate : best));
        const amount = Number(lowest.price);
        if (!Number.isFinite(amount)) throw new Error('price');
        const next = amount <= 0 ? 'Free' : formatShipMoney(amount, row.dataset.currency || 'USD');
        if (value.textContent !== next) value.textContent = next;
        if (amount <= 0) row.querySelector('.lmt-cart__ship-hint')?.remove();
      })
      .catch(() => {
        if (row.dataset.lmtShipReq !== requestId) return;
        if (value.textContent !== 'Calculated at checkout') value.textContent = 'Calculated at checkout';
      });
  });
}

function revealMorePay() {
  document.querySelectorAll('[data-lmt-wallets]').forEach((box) => {
    const more = box.parentElement?.querySelector('[data-lmt-more-pay]');
    if (!more || box.classList.contains('is-open') || !box.dataset.lmtAllWallets) return;
    let extra = false;
    try {
      const all = JSON.parse(box.dataset.lmtAllWallets);
      extra = Array.isArray(all) && all.length > 2;
    } catch (error) {
      return;
    }
    more.hidden = !extra;
  });
}

function watchWallets() {
  document.querySelectorAll('[data-lmt-wallets]').forEach((box) => {
    if (box.dataset.watched === 'true') return;
    box.dataset.watched = 'true';
    new MutationObserver(() => revealMorePay()).observe(box, { childList: true, subtree: true });
  });
  window.setTimeout(revealMorePay, 700);
  window.setTimeout(revealMorePay, 1800);
}

async function swapCartSize(button) {
  const EventsModule = await eventsReady;
  const addUrl = ajaxRoute(routes().cart_add_url, '/cart/add.js');
  const changeUrl = ajaxRoute(routes().cart_change_url, '/cart/change.js');
  const quantity = Number(button.dataset.qty) || 1;
  let resolve = () => {};
  let reject = () => {};
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  promise.catch(() => {});
  button.dispatchEvent(
    new EventsModule.CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [{ id: button.dataset.key, quantity }],
      promise,
    })
  );

  const addedResponse = await fetch(addUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ items: [{ id: Number(button.dataset.variant), quantity }] }),
  });
  const added = await addedResponse.json();
  if (!addedResponse.ok || added.status) {
    const error = new Error(added.description || added.message || 'Could not change size');
    reject(error);
    throw error;
  }

  const changedResponse = await fetch(changeUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({
      id: button.dataset.key,
      quantity: 0,
      sections: 'cart-drawer-section',
      sections_url: `${window.location.pathname}${window.location.search}`,
    }),
  });
  const changed = await changedResponse.json();
  if (!changedResponse.ok || changed.errors || changed.status) {
    const page = `${window.location.pathname}${window.location.search}`;
    const glue = page.includes('?') ? '&' : '?';
    const [cart, sections] = await Promise.all([
      fetch(ajaxRoute(routes().cart_url, '/cart.js'), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((response) => response.json()),
      fetch(`${page}${glue}sections=cart-drawer-section`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((response) => (response.ok ? response.json() : null)),
    ]);
    let cartPayload = { totalQuantity: cart.item_count || 0 };
    try {
      cartPayload = EventsModule.CartLinesUpdateEvent.createCartFromAjaxResponse(cart);
    } catch (error) {
      cartPayload = { totalQuantity: cart.item_count || 0 };
    }
    resolve({
      cart: cartPayload,
      detail: {
        items: cart.items,
        source: 'lmt-cart-size',
        itemCount: cart.item_count || 0,
        didError: false,
        sections,
      },
    });
    return;
  }

  let cartPayload = { totalQuantity: changed.item_count || 0 };
  try {
    cartPayload = EventsModule.CartLinesUpdateEvent.createCartFromAjaxResponse(changed);
  } catch (error) {
    cartPayload = { totalQuantity: changed.item_count || 0 };
  }
  resolve({
    cart: cartPayload,
    detail: {
      items: changed.items,
      source: 'lmt-cart-size',
      itemCount: changed.item_count || 0,
      didError: false,
      sections: changed.sections,
    },
  });
}

function bindCartDrawer() {
  if (bindCartDrawer.bound) return;
  bindCartDrawer.bound = true;

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const edit = target.closest('[data-lmt-cart-edit]');
    if (edit) {
      const sizes = edit.parentElement?.querySelector('.lmt-cart__sizes');
      if (!sizes) return;
      const open = sizes.hasAttribute('hidden');
      sizes.toggleAttribute('hidden', !open);
      edit.setAttribute('aria-expanded', open ? 'true' : 'false');
      return;
    }

    const size = target.closest('[data-lmt-cart-size]');
    if (size instanceof HTMLButtonElement && !size.disabled) {
      const panel = size.closest('.lmt-cart__sizes');
      if (panel?.dataset.busy === 'true') return;
      if (panel) panel.dataset.busy = 'true';
      size.setAttribute('aria-busy', 'true');
      swapCartSize(size).catch(() => {
        if (panel) panel.dataset.busy = 'false';
        size.removeAttribute('aria-busy');
      });
      return;
    }

    const add = target.closest('[data-lmt-cart-add]');
    if (add instanceof HTMLButtonElement) {
      if (add.dataset.busy === 'true') return;
      const quickData = add.closest('.lmt-cart-upsell')?.querySelector('[data-lmt-quick-data]');
      if (quickData) {
        try {
          openCardQuick({ data: JSON.parse(quickData.textContent) });
          return;
        } catch (error) {}
      }
      add.dataset.busy = 'true';
      add.setAttribute('aria-busy', 'true');
      const label = add.textContent;
      add.textContent = 'Adding';
      addLinesToCart([{ id: Number(add.dataset.variant), quantity: 1 }], add).catch(() => {
        add.dataset.busy = 'false';
        add.removeAttribute('aria-busy');
        add.textContent = label;
      });
      return;
    }

    const more = target.closest('[data-lmt-more-pay]');
    if (more instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      const wallets = more.parentElement?.querySelector('[data-lmt-wallets]');
      if (!wallets) return;
      const open = wallets.classList.toggle('is-open');
      more.setAttribute('aria-expanded', open ? 'true' : 'false');
      more.textContent = open ? 'Fewer payment options' : 'More payment options';
      if (typeof window.lmtSetCartWallets === 'function') window.lmtSetCartWallets(wallets, open);
    }
  });

  const schedule = () => {
    window.clearTimeout(bindCartDrawer.timer);
    bindCartDrawer.timer = window.setTimeout(() => {
      updateCartShipping();
      watchWallets();
      revealMorePay();
    }, 60);
  };
  schedule();
  const drawer = document.getElementById('cart-drawer');
  if (drawer) new MutationObserver(schedule).observe(drawer, { childList: true, subtree: true });
  eventsReady
    .then(({ StandardEvents }) => {
      document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
        const run = () => schedule();
        if (event.promise) event.promise.then(run).catch(run);
        else run();
      });
    })
    .catch(() => {});
}

bindCartFeedback();
bindVariantChoice();
bindCollectionCrumb();
bindShare();
placeCollectionChip();
primeUpcomingImages();

function placeCollectionChip() {
  const chip = document.querySelector('.product-information .lmt-collection-chip');
  const frame = document.querySelector('.product-information media-gallery slideshow-component > slideshow-container');
  if (!chip || !frame || chip.parentElement === frame) return;
  frame.append(chip);
}
document.addEventListener('shopify:section:load', () => {
  placeCollectionChip();
  primeUpcomingImages();
});

function bindCollectionCrumb() {
  const show = () => {
    if (!document.querySelector('.product-information')) return;
    if (document.querySelector('.product-information .lmt-crumb')) return;
    if (new URLSearchParams(window.location.search).get('collection') !== 'all') return;
    const host = document.querySelector('.product-information__grid');
    if (!host) return;
    const title = document.querySelector('.product-information h1')?.textContent.replace(/\s+/g, ' ').trim() || '';
    const nav = document.createElement('nav');
    nav.className = 'lmt-crumb';
    nav.setAttribute('aria-label', 'Breadcrumb');
    const link = document.createElement('a');
    link.href = '/collections/all';
    link.textContent = 'Shop all';
    const separator = document.createElement('span');
    separator.setAttribute('aria-hidden', 'true');
    separator.textContent = '/';
    const current = document.createElement('span');
    current.setAttribute('aria-current', 'page');
    current.textContent = title;
    nav.append(link, separator, current);
    const media = host.querySelector('.product-information__media');
    if (media) media.before(nav);
    else host.prepend(nav);
  };
  show();
  document.addEventListener('shopify:product:select', (event) => {
    if (event.promise) event.promise.then(show).catch(show);
    else show();
  });
}

function bindVariantChoice() {
  const choices = new WeakMap();

  const optionNameFrom = (input) => {
    if (input instanceof HTMLSelectElement) return input.selectedOptions[0]?.dataset.optionName || '';
    return input.dataset.optionName || '';
  };

  const ready = (picker) => {
    if (!(picker instanceof HTMLElement) || picker.dataset.lmtChoice !== 'true') return true;
    const groups = picker.querySelectorAll('fieldset.variant-option, .variant-option--dropdowns');
    if (!groups.length) return true;
    for (const group of groups) {
      const select = group.querySelector('select');
      if (select) {
        if (!select.value) return false;
        continue;
      }
      if (!group.querySelector('input[type="radio"]:checked')) return false;
    }
    return true;
  };
  window.lmtVariantReady = ready;

  const restoreChoices = (picker) => {
    const map = choices.get(picker) || new Map();
    picker.querySelectorAll('fieldset.variant-option, .variant-option--dropdowns').forEach((group) => {
      const optionName = group.querySelector('[data-option-name]')?.getAttribute('data-option-name') || '';
      const wanted = map.get(optionName) || '';
      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        const on = wanted !== '' && input.value === wanted;
        input.checked = on;
        input.dataset.currentChecked = on ? 'true' : 'false';
        input.dataset.previousChecked = 'false';
        if (!on) input.removeAttribute('data-variant-id');
      });
      const select = group.querySelector('select');
      if (select) {
        const blank = [...select.options].some((option) => option.value === '');
        if (wanted || blank) select.value = wanted;
      }
      const label = group.querySelector('.lmt-color-option__value, .variant-option__swatch-value');
      if (label) label.textContent = wanted;
    });
    if (typeof picker.updateVariantPickerCss === 'function') picker.updateVariantPickerCss();
  };

  const dialog = document.createElement('dialog');
  dialog.className = 'lmt-options';
  dialog.innerHTML = '<div class="lmt-options__head"><p>Choose options</p><button type="button" class="lmt-options__close" data-lmt-options-close aria-label="Close">&times;</button></div><div class="lmt-options__body"></div><button type="button" class="lmt-options__add" data-lmt-options-add disabled>Add to cart</button>';
  const selectionValues = (picker) => {
    const values = [];
    picker.querySelector('form')?.querySelectorAll(':scope > .variant-option').forEach((group) => {
      const select = group.querySelector('select');
      if (select) {
        values.push(select.value || '');
        return;
      }
      const checked = group.querySelector('input[type="radio"]:checked');
      if (checked) {
        values.push(checked.value);
        return;
      }
      values.push(group.dataset.optionValue || '');
    });
    return values;
  };
  const selectionVariant = (picker) => {
    const script = picker.querySelector('[data-lmt-variant-media]');
    if (!script) return null;
    let rows = [];
    try {
      rows = JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
    const selected = selectionValues(picker);
    if (!selected.length || selected.some((value) => !value)) return null;
    return rows.find((row) => Array.isArray(row.options) && row.options.length === selected.length && row.options.every((option, index) => option === selected[index])) || null;
  };
  const syncChoiceAdd = () => {
    const button = dialog.querySelector('[data-lmt-options-add]');
    const picker = dialog.querySelector('variant-picker');
    if (!(button instanceof HTMLButtonElement) || !(picker instanceof HTMLElement)) return;
    const variant = selectionVariant(picker);
    const soldOut = Boolean(variant && variant.available === false);
    button.disabled = !ready(picker) || !variant?.id || soldOut;
    button.textContent = soldOut ? 'Sold out' : 'Add to cart';
  };
  const finishChoice = () => {
    const picker = dialog.querySelector('variant-picker');
    const form = dialog._form;
    if (!(picker instanceof HTMLElement) || !(form instanceof HTMLFormElement) || !ready(picker)) return;
    const variant = selectionVariant(picker);
    const input = form.querySelector('input[name="id"]');
    if (input && variant?.id) input.value = String(variant.id);
    const submitButton = form.querySelector('[type="submit"][name="add"]');
    if (submitButton) submitButton.disabled = false;
    dialog._onComplete = null;
    dialog.close();
    window.setTimeout(() => {
      if (typeof form.requestSubmit === 'function') form.requestSubmit(submitButton || undefined);
    }, 0);
  };
  dialog.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-lmt-options-close]')) dialog.close();
    if (event.target.closest('[data-lmt-options-add]')) finishChoice();
  });
  dialog.addEventListener('close', () => {
    const picker = dialog.querySelector('variant-picker');
    const marker = dialog._marker;
    if (picker && marker?.parentNode) marker.parentNode.insertBefore(picker, marker);
    marker?.remove();
    dialog._marker = null;
    dialog._onComplete = null;
  });

  const openChoiceSheet = (picker, form) => {
    const section = picker.closest('.product-information') || picker.closest('.shopify-section') || document.body;
    if (dialog.parentElement !== section) section.appendChild(dialog);
    if (!dialog.open) {
      const marker = document.createComment('lmt-picker');
      picker.before(marker);
      dialog._marker = marker;
      dialog.querySelector('.lmt-options__body').appendChild(picker);
      dialog.showModal();
    }
    dialog._form = form;
    syncChoiceAdd();
  };
  const missingOptions = (picker) => {
    const missing = [];
    picker.querySelectorAll('fieldset.variant-option, .variant-option--dropdowns').forEach((group) => {
      const select = group.querySelector('select');
      const name = group.querySelector('[data-option-name]')?.getAttribute('data-option-name') || 'option';
      if (select) {
        if (!select.value) missing.push(name);
        return;
      }
      if (!group.querySelector('input[type="radio"]:checked')) missing.push(name);
    });
    return missing;
  };
  const choiceMessage = (missing) => {
    const names = missing.map((name) => name.toLowerCase());
    const color = names.includes('color') || names.includes('colour');
    const size = names.includes('size');
    const rest = names.filter((name) => name !== 'color' && name !== 'colour' && name !== 'size');
    if (color && size && !rest.length) return 'Select a color and size';
    if (color && !rest.length) return 'Select a color';
    if (size && !rest.length) return 'Select a size';
    if (names.length) return `Select a ${names.join(' and ')}`;
    return 'Select a variant';
  };
  const formFor = (picker) => picker.closest('.shopify-section, featured-product-information, .quick-add-modal')?.querySelector('form[data-type="add-to-cart-form"]') || null;
  const showFormError = (form, text) => {
    const slot = form?.querySelector('[ref="addToCartTextError"]');
    if (!(slot instanceof HTMLElement)) return;
    let message = slot.querySelector('.lmt-choice-error-inline');
    if (!message) {
      message = document.createElement('span');
      message.className = 'lmt-choice-error-inline';
      slot.append(message);
    }
    message.textContent = text;
    slot.classList.remove('hidden');
  };
  const clearChoiceError = (picker) => {
    const slot = formFor(picker)?.querySelector('[ref="addToCartTextError"]');
    slot?.classList.add('hidden');
    slot?.querySelector('.lmt-choice-error-inline')?.remove();
  };
  const askForVariant = (picker) => {
    showFormError(formFor(picker), choiceMessage(missingOptions(picker)));
  };
  window.lmtAskForVariant = askForVariant;
  window.lmtOpenVariantChoice = openChoiceSheet;

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[name="add"], [ref="addToCartButton"]') : null;
    if (!(button instanceof HTMLElement)) return;
    const form = button.closest('form[data-type="add-to-cart-form"]');
    if (!form) return;
    const picker = form.closest('.shopify-section, featured-product-information, .quick-add-modal')?.querySelector('variant-picker[data-lmt-choice]');
    if (!picker || ready(picker)) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    if (button.dataset.puppet === 'true' || button.closest('sticky-add-to-cart')) openChoiceSheet(picker, form);
    else askForVariant(picker);
  }, true);

  document.addEventListener('change', (event) => {
    if (!event.isTrusted) return;
    const input = event.target;
    if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;
    const picker = input.closest('variant-picker[data-lmt-choice]');
    if (!picker) return;
    const optionName = optionNameFrom(input);
    if (!optionName || !input.value) return;
    let map = choices.get(picker);
    if (!map) {
      map = new Map();
      choices.set(picker, map);
    }
    map.set(optionName, input.value);
    if (ready(picker)) clearChoiceError(picker);
    else {
      const inline = formFor(picker)?.querySelector('.lmt-choice-error-inline');
      if (inline) inline.textContent = choiceMessage(missingOptions(picker));
    }
    if (dialog.open) syncChoiceAdd();
  }, true);

  document.addEventListener('shopify:product:select', (event) => {
    const picker = event.target instanceof Element ? event.target.closest('variant-picker[data-lmt-choice]') : null;
    if (!picker || !event.promise) return;
    event.promise.then((result) => {
      restoreChoices(picker);
      const form = formFor(picker);
      const idInput = form?.querySelector('input[name="id"]');
      const complete = ready(picker);
      if (idInput && !complete) idInput.value = '';
      const onProductPage = picker.dataset.templateProductMatch === 'true' && !picker.closest('quick-add-dialog, product-card');
      if (onProductPage) {
        const url = new URL(window.location.href);
        const variantId = complete ? result?.detail?.resource?.id : '';
        if (variantId) url.searchParams.set('variant', variantId);
        else url.searchParams.delete('variant');
        if (url.href !== window.location.href) history.replaceState({}, '', url.toString());
      }
      if (dialog.open) syncChoiceAdd();
    });
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.getAttribute('data-type') !== 'add-to-cart-form') return;
    const picker = form.closest('.shopify-section, featured-product-information')?.querySelector('variant-picker[data-lmt-choice]');
    if (!picker || ready(picker)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (event.submitter?.dataset.puppet === 'true') openChoiceSheet(picker, form);
    else askForVariant(picker);
  }, true);
}

document.addEventListener('click', (event) => {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
  const link = path.find((node) => node instanceof Element && node.id === 'more-payment-options-link')
    || (event.target instanceof Element ? event.target.closest('#more-payment-options-link') : null);
  if (!(link instanceof Element) || link.closest('#cart-drawer, [data-lmt-wallets]')) return;
  const block = link.closest('.accelerated-checkout-block');
  if (!block || typeof window.lmtMountExtraWallets !== 'function') return;
  const source = document.querySelector('#lmt-wallet-source shopify-accelerated-checkout-cart, #cart-drawer shopify-accelerated-checkout-cart');
  // wallet-configs on the cart is trimmed to Shop + Apple/Google. The full list lives on data-lmt-all-wallets.
  const lists = [
    document.querySelector('[data-lmt-all-wallets]')?.getAttribute('data-lmt-all-wallets'),
    source?.getAttribute('wallet-configs'),
  ];
  let configs = [];
  lists.forEach((raw) => {
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > configs.length) configs = parsed;
    } catch (error) {}
  });
  const extras = configs.filter((config) => config && config.name && config.name !== 'shop_pay');
  if (!extras.length) return;
  event.preventDefault();
  event.stopPropagation();
  const grid = block.querySelector(':scope > .lmt-pay-grid');
  if (grid) {
    grid.remove();
    link.textContent = 'More payment options';
    return;
  }
  // Keep the grid on the block. Shopify only displays its own slots inside the payment button.
  window.lmtMountExtraWallets(block, extras, source);
  link.textContent = 'Fewer payment options';
}, true);
bindCartDrawer();
bindWelcome();

function bindWelcome() {
  const root = document.querySelector('[data-lmt-welcome]');
  if (!root) return;
  const dialog = root.querySelector('[data-lmt-welcome-dialog]');
  const tab = root.querySelector('[data-lmt-welcome-tab]');
  if (!dialog || !tab) return;

  if (root.querySelector('[data-lmt-welcome-joined]')) {
    root.dataset.joined = 'true';
    root.dataset.forceOpen = 'true';
  } else if (root.querySelector('[data-lmt-welcome-error]')) {
    root.dataset.forceOpen = 'true';
  }

  const seenKey = 'lmt-welcome-seen';
  const tabKey = 'lmt-welcome-tab';
  const joinedKey = 'lmt-welcome-joined';
  const store = {
    get(key) {
      try { return localStorage.getItem(key); } catch (error) { return null; }
    },
    set(key, value) {
      try { localStorage.setItem(key, value); } catch (error) { /* private mode */ }
    },
  };

  let returnFocus = null;

  const trap = (event) => {
    if (event.key !== 'Tab' || dialog.hidden) return;
    const items = [...dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea')];
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const open = (remember) => {
    returnFocus = document.activeElement;
    dialog.hidden = false;
    tab.hidden = true;
    document.documentElement.classList.add('lmt-welcome-open');
    if (remember) store.set(seenKey, '1');
    const focus = dialog.querySelector('input[type="email"], [data-lmt-welcome-close]');
    focus?.focus();
  };

  const close = () => {
    dialog.hidden = true;
    document.documentElement.classList.remove('lmt-welcome-open');
    store.set(seenKey, '1');
    const joined = root.dataset.joined === 'true' || store.get(joinedKey) === '1';
    if (joined) store.set(joinedKey, '1');
    tab.hidden = joined || store.get(tabKey) === '1';
    if (!tab.hidden) tab.querySelector('[data-lmt-welcome-open]')?.focus();
    else if (returnFocus && typeof returnFocus.focus === 'function') returnFocus.focus();
  };

  if (store.get(joinedKey) === '1' && root.dataset.joined !== 'true') {
    root.hidden = true;
    return;
  }

  tab.hidden = store.get(tabKey) === '1';

  root.querySelectorAll('[data-lmt-welcome-open]').forEach((button) => {
    button.addEventListener('click', () => open(false));
  });
  root.querySelectorAll('[data-lmt-welcome-close]').forEach((button) => {
    button.addEventListener('click', () => close());
  });
  root.querySelector('[data-lmt-welcome-dismiss-tab]')?.addEventListener('click', (event) => {
    event.stopPropagation();
    store.set(tabKey, '1');
    tab.hidden = true;
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !dialog.hidden) close();
    trap(event);
  });

  const popupImage = root.querySelector('.lmt-welcome__img');
  const warmPopup = () => {
    if (!popupImage) return;
    popupImage.fetchPriority = 'low';
    popupImage.loading = 'eager';
  };

  if (root.dataset.forceOpen === 'true') {
    warmPopup();
    open(true);
    return;
  }

  if (store.get(seenKey) === '1') return;
  window.addEventListener('load', warmPopup, { once: true });
  window.setTimeout(() => {
    if (store.get(seenKey) === '1' || store.get(joinedKey) === '1') return;
    open(true);
  }, Number(root.dataset.delay || 5000));
}
